import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { CLINIC, CONDITIONS } from "@/data/clinic";

const linkBase = "text-[15px] font-medium text-slate-700 hover:text-green-700 transition-colors";
const active = "text-green-700";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-100">
      {/* Top bar */}
      <div className="bg-green-700 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-9">
          <span className="hidden sm:block tracking-wide">{CLINIC.shortAddress}</span>
          <div className="flex items-center gap-4">
            <a href={`tel:+91${CLINIC.phone}`} data-testid="topbar-call-link" className="flex items-center gap-1.5 hover:text-green-100">
              <Phone className="w-3.5 h-3.5" />
              <span>{CLINIC.phoneDisplay}</span>
            </a>
            <span className="hidden md:block opacity-80">Mon – Sat • 10 AM – 8:30 PM</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src={CLINIC.logoIcon}
            alt="Dr. Ashutosh Mishra clinic logo"
            className="w-12 h-12 rounded-2xl object-cover shadow-sm"
          />
          <div className="leading-tight">
            <div className="font-poppins font-semibold text-[19px] text-slate-900 tracking-tight">Dr. Ashutosh Mishra</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-green-700 font-medium">Diabetologist & Endocrinologist</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/" end className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`} data-testid="nav-home">Home</NavLink>
          <NavLink to="/about-dr-ashutosh-mishra" className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`} data-testid="nav-about">About</NavLink>

          <div
            className="relative"
            onMouseEnter={() => setTreatmentsOpen(true)}
            onMouseLeave={() => setTreatmentsOpen(false)}
          >
            <button data-testid="nav-treatments-trigger" className={`${linkBase} flex items-center gap-1`}>
              Treatments <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {treatmentsOpen && (
              <div className="absolute top-full left-0 pt-3 w-[320px]">
                <div className="bg-white border border-slate-100 rounded-2xl shadow-xl p-3">
                  <Link to="/diabetologist-varanasi" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-diabetologist">Diabetologist in Varanasi</Link>
                  <Link to="/endocrinologist-varanasi" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-endocrinologist">Endocrinologist in Varanasi</Link>
                  <div className="h-px bg-slate-100 my-2" />
                  {CONDITIONS.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/${c.slug}`}
                      data-testid={`nav-treatment-${c.slug}`}
                      className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700"
                    >
                      {c.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setResourcesOpen(true)}
            onMouseLeave={() => setResourcesOpen(false)}
          >
            <button data-testid="nav-resources-trigger" className={`${linkBase} flex items-center gap-1`}>
              Resources <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {resourcesOpen && (
              <div className="absolute top-full left-0 pt-3 w-[220px]">
                <div className="bg-white border border-slate-100 rounded-2xl shadow-xl p-3">
                  <Link to="/blog" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-blog">Health Blog</Link>
                  <Link to="/gallery" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-gallery">Clinic Gallery</Link>
                  <Link to="/patient-testimonials" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-testimonials">Patient Testimonials</Link>
                  <Link to="/research-publications" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-research">Research & Publications</Link>
                  <Link to="/faqs" className="block px-3 py-2 rounded-lg text-[15px] text-slate-700 hover:bg-green-50 hover:text-green-700" data-testid="nav-faqs">FAQs</Link>
                </div>
              </div>
            )}
          </div>

          <NavLink to="/contact" className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-book-btn"
            onClick={() => nav("/contact")}
            className="hidden md:inline-flex bg-green-700 hover:bg-green-800 text-white text-sm font-medium rounded-full px-5 py-2.5 transition-all shadow-sm"
          >
            Book Appointment
          </button>
          <button
            className="lg:hidden p-2 text-slate-700"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
            {[
              ["/", "Home", "mob-home"],
              ["/about-dr-ashutosh-mishra", "About Doctor", "mob-about"],
              ["/diabetologist-varanasi", "Diabetologist in Varanasi", "mob-diab"],
              ["/endocrinologist-varanasi", "Endocrinologist in Varanasi", "mob-endo"],
              ["/blog", "Health Blog", "mob-blog"],
              ["/videos", "Videos", "mob-videos"],
              ["/gallery", "Gallery", "mob-gallery"],
              ["/patient-testimonials", "Patient Testimonials", "mob-test"],
              ["/research-publications", "Research", "mob-research"],
              ["/faqs", "FAQs", "mob-faqs"],
              ["/contact", "Contact", "mob-contact"],
            ].map(([to, label, id]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={() => setOpen(false)}
                data-testid={id}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-base ${isActive ? "bg-green-50 text-green-700" : "text-slate-700"}`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="border-t border-slate-100 my-2 pt-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 px-3 mb-1">Treatments</div>
              {CONDITIONS.map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mob-treatment-${c.slug}`}
                  className="block px-3 py-2 text-sm text-slate-600 hover:text-green-700"
                >
                  {c.title}
                </Link>
              ))}
            </div>
            <button
              data-testid="mob-book-btn"
              onClick={() => {
                setOpen(false);
                nav("/contact");
              }}
              className="mt-3 bg-green-700 text-white rounded-full py-2.5 text-sm font-medium"
            >
              Book Appointment
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
